import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { getBatchCheckStatus, startAsyncBatchCheck, cancelBatchCheck } from '../api/backlinks';

const RECONNECT_BASE = 3000;
const RECONNECT_MAX = 30000;
const INVALIDATE_DEBOUNCE_MS = 3000; // Debounce query invalidation to max 1 per 3s

/**
 * Hook for managing batch check progress via WebSocket
 */
export function useBatchProgress(projectId, { onComplete } = {}) {
  const queryClient = useQueryClient();
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const invalidateTimerRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [jobState, setJobState] = useState(null);

  const isRunning = jobState?.status === 'running' || jobState?.status === 'starting';

  // Build a Map from queue for O(1) lookups by backlink ID
  const queueMap = useMemo(() => {
    if (!jobState?.queue) return new Map();
    return new Map(jobState.queue.map(q => [q.id, q.status]));
  }, [jobState?.queue]);

  // Derive checkingProgress shape compatible with existing UI
  const checkingProgress = useMemo(() => {
    if (!jobState) {
      return { isRunning: false, type: null, total: 0, completed: 0, failed: 0, queue: [], queueMap: new Map() };
    }
    return {
      isRunning: jobState.status === 'running',
      type: 'check',
      total: jobState.total || 0,
      completed: jobState.completed || 0,
      failed: jobState.failed || 0,
      queue: jobState.queue || [],
      queueMap,
    };
  }, [jobState, queueMap]);

  // Debounced query invalidation - prevents flooding the API
  const debouncedInvalidate = useCallback(() => {
    if (invalidateTimerRef.current) return; // Already scheduled
    invalidateTimerRef.current = setTimeout(() => {
      invalidateTimerRef.current = null;
      queryClient.invalidateQueries(['backlinks', projectId]);
    }, INVALIDATE_DEBOUNCE_MS);
  }, [queryClient, projectId]);

  // Build WebSocket URL
  const getWsUrl = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;

    // Add dev auth if needed
    const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
    const hasValidClerkKey = clerkPubKey && clerkPubKey.startsWith('pk_') && clerkPubKey.length > 30;

    let authParam = '';
    if (!hasValidClerkKey) {
      try {
        const authData = localStorage.getItem('backlinkguard_dev_auth');
        if (authData) {
          const parsed = JSON.parse(authData);
          if (parsed.isSignedIn) {
            authParam = '?devAuth=dev_authenticated';
          }
        }
      } catch { /* ignore */ }
    }

    return `${protocol}//${host}/api/batch/ws/${projectId}${authParam}`;
  }, [projectId]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!projectId) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(getWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          switch (msg.type) {
            case 'job_state':
            case 'job_started':
              // Full state with queue - set directly
              setJobState(msg.data);
              break;

            case 'progress':
              // Lightweight update - merge into existing state
              setJobState(prev => {
                if (!prev) return msg.data;
                const updated = {
                  ...prev,
                  status: msg.data.status,
                  completed: msg.data.completed,
                  failed: msg.data.failed,
                };
                // Apply single item update if provided
                if (msg.data.updatedItem && prev.queue) {
                  updated.queue = prev.queue.map(q =>
                    q.id === msg.data.updatedItem.id
                      ? { ...q, status: msg.data.updatedItem.status }
                      : q
                  );
                } else if (msg.data.queue) {
                  updated.queue = msg.data.queue;
                }
                return updated;
              });
              // Debounced table refresh
              debouncedInvalidate();
              break;

            case 'job_completed':
              setJobState(msg.data);
              // Immediate invalidation on completion
              queryClient.invalidateQueries(['backlinks', projectId]);
              queryClient.invalidateQueries(['backlink-stats', projectId]);
              toast.success(
                `Checked ${msg.data.completed} backlinks` +
                (msg.data.failed > 0 ? ` (${msg.data.failed} failed)` : '')
              );
              // Call onComplete callback if provided (for chaining full check steps)
              if (onCompleteRef.current) {
                onCompleteRef.current(msg.data);
              }
              setTimeout(() => setJobState(null), 2000);
              break;

            case 'job_cancelled':
              setJobState(msg.data);
              queryClient.invalidateQueries(['backlinks', projectId]);
              toast('Batch check cancelled');
              setTimeout(() => setJobState(null), 1500);
              break;

            case 'job_error':
              toast.error(`Batch check error: ${msg.data.error}`);
              setJobState(null);
              break;

            case 'no_active_job':
              setJobState(null);
              break;
          }
        } catch { /* ignore parse errors */ }
      };

      ws.onclose = () => {
        wsRef.current = null;
        const delay = Math.min(RECONNECT_BASE * Math.pow(2, reconnectAttemptRef.current), RECONNECT_MAX);
        reconnectAttemptRef.current++;
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        // onclose will fire after onerror, which handles reconnection
      };
    } catch {
      const delay = Math.min(RECONNECT_BASE * Math.pow(2, reconnectAttemptRef.current), RECONNECT_MAX);
      reconnectAttemptRef.current++;
      reconnectTimerRef.current = setTimeout(connect, delay);
    }
  }, [projectId, getWsUrl, queryClient, debouncedInvalidate]);

  // Connect on mount, reconnect on projectId change
  useEffect(() => {
    if (projectId) {
      getBatchCheckStatus(projectId)
        .then((data) => {
          if (data.active && data.job) {
            setJobState(data.job);
          }
        })
        .catch(() => { /* ignore */ });
    }

    connect();

    return () => {
      clearTimeout(reconnectTimerRef.current);
      clearTimeout(invalidateTimerRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [projectId, connect]);

  // Start a batch check
  const startBatchCheck = useCallback(async (mode, backlinkIds) => {
    try {
      await startAsyncBatchCheck(projectId, mode, backlinkIds);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start batch check');
    }
  }, [projectId]);

  // Cancel a batch check
  const cancelBatch = useCallback(async () => {
    if (jobState?.jobId) {
      try {
        await cancelBatchCheck(jobState.jobId);
      } catch {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'cancel', jobId: jobState.jobId }));
        }
      }
    }
  }, [jobState?.jobId]);

  return {
    jobState,
    isRunning,
    checkingProgress,
    startBatchCheck,
    cancelBatch,
  };
}
